import retryCallIfNeeded from '../../../restCalls';
import logger from '../../../utils/logger';
import createStudyComOperation, { studyComErrorCode } from './studyComTracking';

const isUncertainStudyComFailure = error => !error?.response && (
  error?.message === 'Network Error'
  || ['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code)
);

export default function SalesforceService(client) {
  // Keep a read/clear after a program change behind any acknowledgement already being saved.
  const pendingStudyComUpdates = new Map();
  const waitForStudyComUpdate = async applicationId => {
    try {
      await pendingStudyComUpdates.get(applicationId);
    } catch { /* A subsequent read reconciles an uncertain write with SRM. */ }
  };

  // Read without waiting on the write queue so a write can reconcile its own result.
  const readStudyComInterest = async (applicationId, track, phase = 'read') => {
    let attempt = 0;
    const read = async () => {
      attempt += 1;
      const details = { phase, method: 'GET', attempt };
      track('request_started', details);
      try {
        const response = await client.get('/srm/opportunity', {
          params: { applicationId, intent: 'study-com' },
          // Suppress interceptor replay; this read owns a single retry below.
          boundedRetry: true,
          timeout: 15000,
        });
        track('request_succeeded', details, response);
        return response;
      } catch (error) {
        track('request_failed', { ...details, errorCode: studyComErrorCode(error) }, error);
        throw error;
      }
    };
    try {
      return (await read()).data;
    } catch (error) {
      if (!isUncertainStudyComFailure(error)) throw error;
      track('read_retry', { phase, method: 'GET', attempt: 2 });
      return (await read()).data;
    }
  };

  const getStudyComInterest = async applicationId => {
    const track = createStudyComOperation('load');
    track('started');
    try {
      await waitForStudyComUpdate(applicationId);
      const data = await readStudyComInterest(applicationId, track);
      const valid = data?.applicationId === applicationId && typeof data?.Studycom__c === 'boolean';
      track(valid ? 'succeeded' : 'failed', valid ? {} : { errorCode: 'INVALID_RESPONSE' });
      return data;
    } catch (error) {
      track('failed', { errorCode: studyComErrorCode(error) }, error);
      throw error;
    }
  };

  const updateStudyComInterest = async (applicationId, interested) => {
    const track = createStudyComOperation('save');
    const writeDetails = { phase: 'write', method: 'PATCH', attempt: 1 };
    track('started');
    const update = waitForStudyComUpdate(applicationId).then(async () => {
      try {
        track('request_started', writeDetails);
        const response = await client.patch('/srm/opportunity', {
          applicationId,
          Studycom__c: interested,
        }, {
          params: { intent: 'study-com' },
          // Never automatically replay an acknowledgement write.
          boundedRetry: true,
          timeout: 15000,
        });
        track('request_succeeded', writeDetails, response);
        const valid = response.data?.applicationId === applicationId
          && response.data?.Studycom__c === interested;
        track(valid ? 'succeeded' : 'failed', valid ? {} : { errorCode: 'INVALID_RESPONSE' }, response);
        return response.data;
      } catch (error) {
        track('request_failed', { ...writeDetails, errorCode: studyComErrorCode(error) }, error);
        if (!isUncertainStudyComFailure(error)) {
          track('failed', { errorCode: studyComErrorCode(error) }, error);
          throw error;
        }
        track('reconciliation_started');
        try {
          const latest = await readStudyComInterest(applicationId, track, 'reconciliation');
          // This confirms the observed value, not completion of the original server write.
          if (latest?.applicationId === applicationId && latest.Studycom__c === interested) {
            track('reconciliation_confirmed');
            track('succeeded', { phase: 'reconciliation' });
            return latest;
          }
          track('reconciliation_unconfirmed');
        } catch (readError) {
          track('reconciliation_failed', { errorCode: studyComErrorCode(readError) }, readError);
          // Keep the original save error when reconciliation is unavailable.
        }
        track('failed', { errorCode: studyComErrorCode(error) }, error);
        throw error;
      }
    });
    pendingStudyComUpdates.set(applicationId, update);
    try {
      return await update;
    } finally {
      if (pendingStudyComUpdates.get(applicationId) === update) {
        pendingStudyComUpdates.delete(applicationId);
      }
    }
  };

  const getERInformation = async () => {
    const response = await retryCallIfNeeded(
      client,
      () => client.get('/srm/opportunity'),
    );
    const data = response?.data;
    return data;
  };

  const updateOpportunityData = async opportunityData => {
    try {
      const response = await retryCallIfNeeded(
        client,
        () => client.patch('/srm/opportunity', opportunityData),
      );
      const data = response?.data;
      return data;
    } catch (error) {
      logger.error('[SAC] updateOpportunityData failed:', `${error?.message} status=${error?.response?.status}`);
      throw error;
    }
  };

  const getApplicationData = async () => {
    const response = await retryCallIfNeeded(
      client,
      () => client.get('/srm/application'),
    );
    const data = response?.data;
    return data;
  };

  const getREGClearData = async () => {
    const response = await retryCallIfNeeded(
      client,
      () => client.get('/srm/regclear'),
    );
    const data = response?.data;
    return data;
  };

  const getAccountData = async () => {
    const response = await retryCallIfNeeded(
      client,
      () => client.get('/srm/account'),
    );
    const data = response?.data;
    return data;
  };

  const updateAccountData = async accountData => {
    const response = await retryCallIfNeeded(
      client,
      () => client.patch('/srm/account', accountData),
    );
    const data = response?.data;
    return data;
  };

  return {
    getStudyComInterest,
    updateStudyComInterest,
    getERInformation,
    updateOpportunityData,
    getApplicationData,
    getREGClearData,
    getAccountData,
    updateAccountData,
  };
}
